import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Routes, BrowserRouter, Route, useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';

import EsignLoanDocs from 'containers/esign-loan-documents';
import { useConfig } from 'config/ConfigContext';

import PickPaymentDate from 'containers/reprompt/PickPaymentDate';
import Landing from './containers/landing/Landing';
import LoanDetails from './containers/loan-details/LoanDetails';
import Security from './containers/security/Security';
import IDASecurity from './containers/security/IDASecurity';
import Welcome from './containers/verification/welcome';
import SecurityCameraAccess from './containers/security/SecurityCameraAccess';
import SecurityError from './containers/security/SecurityError';
import Review from './containers/review/Review';
import PreApproved from './containers/results/PreApproved';
import Pending from './containers/results/Pending';
import Incomplete from './containers/results/Incomplete';
import Denied from './containers/results/Denied';
import CreditFreeze from './containers/results/CreditFreeze';
import ManualVerification from './containers/results/ManualVerification';
import Timeout from './containers/results/Timeout';
import Blocked from './containers/results/Blocked';
import Continue from './containers/results/Continue';
import DPOnlyLoanOptions from './containers/dp-only/loan-options/LoanOptions';
import Verify from './containers/payment/Plaid';
import FindBank from './containers/payment/FindBank';
import ConnectBank from './containers/payment/ConnectBank';
import Manual from './containers/payment/Manual';
import ACH from './containers/payment/ACH';
import DebitCardElection from './containers/payment/DebitCardElection';
import DPOnlyDebitCardElection from './containers/dp-only/payment/DebitCardElection';
import Check from './containers/payment/Check';
import Confirmed from './containers/payment/Confirmed';
import AgreementSign from './containers/agreement/Sign';
import AgreementComplete from './containers/agreement/Complete';
import AgreementDeclined from './containers/agreement/Declined';
import DuplicateDeclined from './containers/results/DuplicateDeclined';
import FundedDuplicate from './containers/results/FundedDuplicate';
import Definitions from './containers/agreement/Definitions';
import VerifyID from './containers/agreement/VerifyID';
import Complete from './containers/complete/Complete';
import IDALanding from './containers/ida/idaLanding';
import RepromptLastName from './containers/reprompt/LastName';
import RepromptSSN from './containers/reprompt/SSN';

import Confirmation from './containers/identity/confirmation';
import Questions from './containers/identity/questions';
// Microbilt
import Microbilt from './containers/microbilt/Verification';
import MicrobiltComplete from './containers/microbilt/complete';
// Pliad
import Plaid from './containers/plaid/verification';
// MX
import MX from './containers/mx/verification';
// Verification
import CompleteVerification from './containers/verification/complete';
import CheckinVerification from './containers/verification/checkin';
import IncomeWaiting from './containers/microbilt/waiting';
import Redirection from './containers/redirection';

import GuardedRoute from './components/GuardedRoute';

import './assets/scss/App.scss';

const RouteWrapper = ({ Component }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  return (
    <Component
      location={location}
      navigate={navigate}
      params={params}
      searchParams={searchParams}
    />
  );
};

RouteWrapper.propTypes = {
  Component: PropTypes.func.isRequired,
};

const routes = [
  // Microbilt Workflows
  { path: '/microbilt/verification', element: Microbilt },
  { path: '/microbilt/complete', element: MicrobiltComplete },
  { path: '/:workflowType/income/waiting', element: IncomeWaiting },
  { path: '/income/waiting', element: IncomeWaiting },
  // Plaid Workflows
  { path: '/plaid/verification', element: Plaid },
  // MX Workflows
  { path: '/mx/verification', element: MX },
  // Verification Workflows
  { path: '/verification/checkin', element: CheckinVerification },
  { path: '/:workflowType/welcome', element: Welcome },
  { path: '/ida/complete', element: CompleteVerification },
  { path: '/:workflowType/completed', element: CompleteVerification },
  // Main Routes
  { path: '/:workflowType/loan-details', element: LoanDetails },
  { path: '/loan-details', element: LoanDetails },
  { path: '/:workflowType/ida/security/main', element: IDASecurity },
  { path: '/ida/security/main', element: IDASecurity },
  { path: '/:workflowType/security/main', element: Security },
  { path: '/security/main', element: Security },
  { path: '/:workflowType/security/camera-access', element: SecurityCameraAccess },
  { path: '/security/camera-access', element: SecurityCameraAccess },
  { path: '/:workflowType/security/error', element: SecurityError },
  { path: '/security/error', element: SecurityError },
  { path: '/:workflowType/review', element: Review },
  { path: '/review', element: Review },
  // Results
  { path: '/:workflowType/results/preapproved', element: PreApproved },
  { path: '/results/preapproved', element: PreApproved },
  { path: '/:workflowType/results/pending', element: Pending },
  { path: '/results/pending', element: Pending },
  { path: '/:workflowType/results/incomplete', element: Incomplete },
  { path: '/results/incomplete', element: Incomplete },
  { path: '/:workflowType/results/denied', element: Denied },
  { path: '/results/denied', element: Denied },
  { path: '/:workflowType/results/denied2', element: Blocked },
  { path: '/results/denied2', element: Blocked },
  { path: '/:workflowType/results/continue', element: Continue },
  { path: '/results/continue', element: Continue },
  { path: '/:workflowType/results/duplicate-declined', element: DuplicateDeclined },
  { path: '/results/duplicate-declined', element: DuplicateDeclined },
  { path: '/:workflowType/results/funded-duplicate', element: FundedDuplicate },
  { path: '/results/funded-duplicate', element: FundedDuplicate },
  { path: '/:workflowType/results/creditfreeze', element: CreditFreeze },
  { path: '/results/creditfreeze', element: CreditFreeze },
  // Loan Options
  { path: '/:workflowType/loan-options', element: DPOnlyLoanOptions },
  { path: '/loan-options', element: DPOnlyLoanOptions },
  // Payment
  { path: '/:workflowType/payment/verify', element: Verify },
  { path: '/payment/verify', element: Verify },
  { path: '/:workflowType/payment/plaid', element: FindBank },
  { path: '/payment/plaid', element: FindBank },
  { path: '/:workflowType/payment/connect', element: ConnectBank },
  { path: '/payment/connect', element: ConnectBank },
  { path: '/:workflowType/payment/manual', element: Manual },
  { path: '/payment/manual', element: Manual },
  { path: '/:workflowType/payment/ach', element: ACH },
  { path: '/payment/ach', element: ACH },
  { path: '/:workflowType/payment/debitcardelection', element: DPOnlyDebitCardElection },
  { path: '/payment/debitcardelection', element: DebitCardElection },
  { path: '/:workflowType/payment/check', element: Check },
  { path: '/payment/check', element: Check },
  { path: '/:workflowType/payment/confirmed', element: Confirmed },
  { path: '/payment/confirmed', element: Confirmed },
  // Contract & Agreement
  { path: '/:workflowType/contract/sign', element: EsignLoanDocs },
  { path: '/contract/sign', element: EsignLoanDocs },
  { path: '/:workflowType/agreement/complete', element: AgreementComplete },
  { path: '/agreement/complete', element: AgreementComplete },
  { path: '/:workflowType/agreement/sign', element: AgreementSign },
  { path: '/agreement/sign', element: AgreementSign },
  { path: '/:workflowType/agreement/declined', element: AgreementDeclined },
  { path: '/agreement/declined', element: AgreementDeclined },
  { path: '/:workflowType/agreement/definitions', element: Definitions },
  { path: '/agreement/definitions', element: Definitions },
  { path: '/:workflowType/agreement/verify-id', element: VerifyID },
  { path: '/agreement/verify-id', element: VerifyID },
  // IDA
  { path: '/ida/landing', element: IDALanding },
  // Complete & Reprompt
  { path: '/:workflowType/complete', element: Complete },
  { path: '/complete', element: Complete },
  { path: '/:workflowType/reprompt/name', element: RepromptLastName },
  { path: '/reprompt/name', element: RepromptLastName },
  { path: '/:workflowType/reprompt/ssn', element: RepromptSSN },
  { path: '/reprompt/ssn', element: RepromptSSN },
  { path: '/:workflowType/pick-payment-date', element: PickPaymentDate },
  { path: '/pick-payment-date', element: PickPaymentDate },
  // Identity
  { path: '/application/:workflowType/identity/confirmation', element: Confirmation },
  { path: '/application/identity/confirmation', element: Confirmation },
  { path: '/application/:workflowType/identity/questions', element: Questions },
  { path: '/application/identity/questions', element: Questions },
  { path: '/applications/:workflowType/identity/manual-verification', element: ManualVerification },
  { path: '/applications/identity/manual-verification', element: ManualVerification },
  { path: '/applications/:workflowType/identity/timeout', element: Timeout },
  { path: '/applications/identity/timeout', element: Timeout },
  { path: '/applications/:workflowType/redirection', element: Redirection },
  { path: '/applications/redirection', element: Redirection },
  // Landing
  { path: '/', element: Landing, exact: true },
  { path: '/:workflowType', element: Landing, exact: true },
];

const AppRoutes = () => (
  <Routes>
    {routes.map((route, index) => (
      <Route
        key={index}
        path={route.path}
        element={(
          <GuardedRoute>
            <RouteWrapper Component={route.element} />
          </GuardedRoute>
        )}
        {...(route.exact && { exact: route.exact })}
      />
    ))}
  </Routes>
);

const App = () => {
  const appConfig = useConfig();

  const addPageSenseScript = () => {
    const scriptTag = document.createElement('script');
    scriptTag.src = `https://cdn.pagesense.io/js/lendingusa/${appConfig.pageSenseId}.js`;
    document.head.appendChild(scriptTag);
  };

  useEffect(() => {
    if (appConfig.pageSenseId) {
      addPageSenseScript();
    }
  }, []);

  return (
    <div>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </div>
  );
};

export default App;
